import { Space, Tag } from 'antd';
import { get } from 'lodash';
import { FC, useMemo } from 'react';
import Table, { ColumnsType } from 'antd/lib/table';
import { EditOutlined } from '@ant-design/icons';
import { StyledTableHeaderCell, StyledTableWrapper } from 'components/new-ui/base/table/styles';
import Button from 'components/new-ui/base/button/Button';
import IconButton from 'components/new-ui/base/icon-button/IconButton';
import { GoodsShipmentItem } from 'store/declarations/ireland/import-declaration';
import { MISSING_LABEL, isRequiredMessage, renderMissingValue } from '../utils';
import { irelandH1ColumnErrorPaths } from '../fix-item-modal/irelandH1ItemFieldsRegistry';
import { getIrelandH1ItemColumns } from './irelandH1ItemColumns';
import { ItemErrors } from '../../types';
import { MissingValue } from '../styles';

interface Props {
    // Expected to only contain items that had errors when the page loaded - filtering happens
    // once, upstream in MirroringPreparationPage, right after validation. Items stay in the table
    // after being fixed, with an empty entry in `errors`.
    data: GoodsShipmentItem[] | undefined | null;
    errors: Record<number, ItemErrors>;
    onQuickFix: (record: GoodsShipmentItem, index: number) => void;
}

// A field the user never filled in is shown as the same "Missing" placeholder used everywhere
// else; anything else (a value that's there but malformed) keeps its own message.
const toDisplayedMessage = (message: string) => (isRequiredMessage(message) ? MISSING_LABEL : message);

const getFieldError = (itemErrors: ItemErrors | undefined, columnKey: string): string | undefined => {
    const paths = irelandH1ColumnErrorPaths[columnKey];
    if (!itemErrors || !paths) return undefined;

    for (const path of paths) {
        const error = get(itemErrors, path);
        if (!error) continue;

        if (typeof error === 'string') return toDisplayedMessage(error);
        if (Array.isArray(error)) {
            const stringError = error.find((entry) => typeof entry === 'string');
            if (stringError) return toDisplayedMessage(stringError);
        }
        // Array/object errors (e.g. packaging entries) don't map to a single message - fall back to a generic one.
        return MISSING_LABEL;
    }
    return undefined;
};

const IrelandH1QuickFixTable: FC<Props> = ({ data, errors, onQuickFix }) => {
    const productColumns = useMemo(() => getIrelandH1ItemColumns(renderMissingValue), []);

    const columns: ColumnsType<GoodsShipmentItem> = useMemo(
        () => [
            ...productColumns.map((column) => ({
                ...column,
                render: (value: any, record: GoodsShipmentItem, index: number) => {
                    const fieldError = getFieldError(errors[index], column.key as string);
                    if (fieldError) return <MissingValue>{fieldError}</MissingValue>;
                    return (column as any).render?.(value, record, index);
                },
            })),
            {
                title: 'Action',
                dataIndex: 'action',
                key: 'action',
                width: 1,
                render: (_: unknown, record: GoodsShipmentItem, index: number) => {
                    const itemErrors = errors[index];
                    if (itemErrors && Object.keys(itemErrors).length === 0) {
                        return (
                            <Space size={4}>
                                <Tag color="success">Fixed</Tag>
                                <IconButton
                                    icon={<EditOutlined />}
                                    tooltipText="Update item"
                                    onClick={() => onQuickFix(record, index)}
                                />
                            </Space>
                        );
                    }
                    return (
                        <Button type="primary" size="small" onClick={() => onQuickFix(record, index)}>
                            Quick Fix
                        </Button>
                    );
                },
            },
        ],
        [productColumns, errors, onQuickFix]
    );

    return (
        <StyledTableWrapper>
            <Table
                rowClassName="row-align-top"
                rowKey="id"
                dataSource={data || []}
                columns={columns}
                pagination={{ position: ['bottomCenter'], pageSize: 10, hideOnSinglePage: true }}
                components={{
                    header: {
                        cell: (props: any) => <StyledTableHeaderCell {...props} />,
                    },
                }}
            />
        </StyledTableWrapper>
    );
};

export default IrelandH1QuickFixTable;
